/** eslint-disable @pmag/react/enqueue-snackbar-variant-error */
import { useMediaQuery } from "@mui/material";
import { Box, useTheme } from "@mui/system";
import { DownloadSave, LoadingAnimated } from "@pmag/icons";
import { apiKeyVar } from "@src/utils/apollo/links/apiKeyLink";
// eslint-disable-next-line import/no-extraneous-dependencies
import { saveAs } from "file-saver";
import { useSnackbar } from "notistack";
import { type FunctionComponent, useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { StyledButton, StyledNotification } from "./DownloadButton.styles";

export const dataTestId = {
    downloadButton: "sparePartsFinder.subgroupPage.controlPanel.button.downloadPDF",
    error: "sparePartsFinder.subgroupPage.controlPanel.button.downloadPDF.error",
};

interface PrintButtonProps {
    fileName: string;
    url: string;
}

export const DownloadButton: FunctionComponent<PrintButtonProps> = ({ url, fileName, ...restProps }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
    const apiKey = apiKeyVar();

    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);

    const snackbar = useSnackbar();

    const saveRemotePdf = useCallback(async () => {
        setLoading(true);

        fetch(url, { headers: { "X-Api-Key": apiKey ?? "" } })
            .then((response) => response.blob())
            .then((blob) => {
                const localUrl = window.URL.createObjectURL(blob);
                saveAs(localUrl, fileName);
            })
            .catch(() => {
                setError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [apiKey, fileName, url]);

    useEffect(() => {
        if (error) {
            // eslint-disable-next-line @pmag/react/enqueue-snackbar-variant-error
            snackbar?.enqueueSnackbar("", {
                variant: "error",
                persist: true,
                content: (key: string) => (
                    <Box data-testid={dataTestId.error}>
                        <StyledNotification severity="error" onClose={() => snackbar.closeSnackbar(key)}>
                            <FormattedMessage
                                id="sparePartsFinder.subgroupPage.controlPanel.button.downloadPDF.error"
                                defaultMessage="PDF loading failed"
                            />
                        </StyledNotification>
                    </Box>
                ),
            });
            setLoading(false);
            setError(false);
        }
    }, [error, snackbar]);

    const icon = loading ? <LoadingAnimated /> : <DownloadSave size="small" />;
    const variant = isSmallScreen ? "text" : "primary";

    return (
        <StyledButton
            data-testid={dataTestId.downloadButton}
            icon={!isSmallScreen ? icon : undefined}
            startIcon={isSmallScreen ? icon : undefined}
            size="small"
            variant={variant}
            onClick={saveRemotePdf}
            disabled={loading}
            {...restProps}
        >
            <FormattedMessage id="sparePartsFinder.subgroupPage.controlPanel.button.downloadPDF" defaultMessage="PDF Download" />
        </StyledButton>
    );
};
